import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import { mergeRecipeTitles } from '../utils/fetchTranslatedRecipes';
import './SwipeView.css';

const LS_KEY = 'solt_pantry_swipe_saved';

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 700);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 700px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

const TIME_OPTIONS = [
  { label: '< 30 min',   labelHu: '< 30 perc',  labelMobile: '30m',  labelMobileHu: '30p',   value: 30 },
  { label: '60 min+',    labelHu: '60 perc+',   labelMobile: '60m+', labelMobileHu: '60p+',  value: 60 },
];

const DIET_OPTIONS = [
  { label: 'Vegetarian', labelHu: 'Vegetáriánus',     labelMobile: 'Veggie',   labelMobileHu: 'Veggie',  key: 'vegetarian' },
  { label: 'Low carb',   labelHu: 'Kevés szénhidrát', labelMobile: 'Low carb', labelMobileHu: 'Low carb', key: 'low-carb' },
];

export default function SwipeView({ onBack, onSelectRecipe, stateRef }) {
  const { user, isAdmin, isMember } = useAuth();
  const { language, switchLanguage } = useLanguage();
  const isDesktop = useIsDesktop();

  const [rawRecipes, setRawRecipes] = useState([]); // shuffled, untranslated
  const [recipes, setRecipes] = useState([]);        // translated display copy
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savedIds, setSavedIds] = useState(new Set());

  // Filters
  const [maxTime, setMaxTime] = useState(null);       // null = any
  const [dietFilters, setDietFilters] = useState(new Set()); // 'vegetarian', 'low-carb'

  // Apply filters to recipes
  const filteredRecipes = recipes.filter(r => {
    const total = (r.prep_time_mins || 0) + (r.cook_time_mins || 0);
    if (maxTime === 30 && total >= 30) return false;
    if (maxTime === 60 && total < 60) return false;
    if (dietFilters.size > 0) {
      const tags = (r.dietary_tags || []).map(t => t.toLowerCase().replace(/\s+/g, '-'));
      for (const f of dietFilters) {
        // 'vegetarian' matches tags containing 'vegetarian'; 'low-carb' matches 'low carb', 'low-carb', etc.
        if (f === 'vegetarian' && !tags.some(t => t.includes('vegetarian'))) return false;
        if (f === 'low-carb' && !tags.some(t => t.includes('low') && t.includes('carb'))) return false;
      }
    }
    return true;
  });

  // Reset index when filters change
  useEffect(() => {
    setIndex(0);
  }, [maxTime, dietFilters]);

  const toggleDiet = useCallback((key) => {
    setDietFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // Fetch raw recipes + saved IDs once on mount (no language dependency)
  useEffect(() => {
    // Restore from ref if we have it (back-navigation)
    if (stateRef?.current) {
      const { rawRecipes: savedRaw, index: savedIndex, savedIds: savedSet } = stateRef.current;
      setRawRecipes(savedRaw);
      setIndex(savedIndex);
      setSavedIds(savedSet);
      setLoading(false);
      return;
    }

    async function load() {
      const { data, error } = await supabase
        .from('recipes')
        .select('id, title, category, description, image_url, difficulty, prep_time_mins, cook_time_mins, dietary_tags');
      if (error || !data) { setLoading(false); return; }

      setRawRecipes(shuffle(data));

      if (isAdmin) {
        const { data: favs } = await supabase.from('recipes').select('id').eq('is_favourite', true);
        setSavedIds(new Set((favs || []).map(r => r.id)));
      } else if (isMember && user) {
        const { data: favs } = await supabase.from('user_favourites').select('recipe_id').eq('user_id', user.id);
        setSavedIds(new Set((favs || []).map(r => r.recipe_id)));
      } else {
        try {
          const stored = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
          setSavedIds(new Set(stored));
        } catch { setSavedIds(new Set()); }
      }

      setLoading(false);
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isMember, user]);

  // Keep ref in sync so back-navigation can restore position
  useEffect(() => {
    if (stateRef && rawRecipes.length > 0) {
      stateRef.current = { rawRecipes, index, savedIds };
    }
  }, [rawRecipes, index, savedIds, stateRef]);

  // Re-apply translations whenever rawRecipes or language changes (no reshuffle, no index reset)
  useEffect(() => {
    if (rawRecipes.length === 0) return;
    async function applyTranslations() {
      let merged = await mergeRecipeTitles(rawRecipes, language);
      if (language === 'hu') {
        const ids = rawRecipes.map(r => r.id);
        const { data: trans } = await supabase
          .from('recipe_translations')
          .select('recipe_id, description')
          .in('recipe_id', ids)
          .eq('language_code', 'hu');
        if (trans && trans.length > 0) {
          const descMap = {};
          trans.forEach(t => { descMap[t.recipe_id] = t.description; });
          merged = merged.map(r => ({ ...r, description: descMap[r.id] || r.description }));
        }
      }
      setRecipes(merged);
    }
    applyTranslations();
  }, [rawRecipes, language]);

  const toggleSave = useCallback(async (recipe) => {
    const isSaved = savedIds.has(recipe.id);
    if (isSaved) {
      setSavedIds(prev => { const n = new Set(prev); n.delete(recipe.id); return n; });
      if (isAdmin) {
        await supabase.from('recipes').update({ is_favourite: false }).eq('id', recipe.id);
      } else if (isMember && user) {
        await supabase.from('user_favourites').delete().eq('user_id', user.id).eq('recipe_id', recipe.id);
      } else {
        try {
          const stored = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
          localStorage.setItem(LS_KEY, JSON.stringify(stored.filter(id => id !== recipe.id)));
        } catch {}
      }
    } else {
      setSavedIds(prev => new Set([...prev, recipe.id]));
      if (isAdmin) {
        await supabase.from('recipes').update({ is_favourite: true }).eq('id', recipe.id);
      } else if (isMember && user) {
        await supabase.from('user_favourites').insert({ user_id: user.id, recipe_id: recipe.id });
      } else {
        try {
          const stored = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
          if (!stored.includes(recipe.id)) {
            localStorage.setItem(LS_KEY, JSON.stringify([...stored, recipe.id]));
          }
        } catch {}
      }
    }
  }, [savedIds, isAdmin, isMember, user]);

  const advance = useCallback(() => {
    setIndex(i => i + 1);
  }, []);

  const goBack = useCallback(() => {
    setIndex(i => Math.max(0, i - 1));
  }, []);

  // Touch swipe handling for mobile
  const touchStartX = useRef(null);
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);
  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) advance();
    else goBack();
  }, [advance, goBack]);

  // Keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { onBack(); return; }
      if (!filteredRecipes[index]) return;
      if (e.key === 'ArrowRight') advance();
      if (e.key === 'ArrowLeft') goBack();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRecipes, index, advance, goBack, onBack]);

  const totalTime = (r) => {
    const t = (r.prep_time_mins || 0) + (r.cook_time_mins || 0);
    return t > 0 ? `${t} min` : null;
  };

  // Filter bar (shared between desktop and mobile)
  const getTimeLabel  = (opt) => isDesktop
    ? (language === 'hu' ? opt.labelHu : opt.label)
    : (language === 'hu' ? opt.labelMobileHu : opt.labelMobile);
  const getDietLabel  = (opt) => isDesktop
    ? (language === 'hu' ? opt.labelHu : opt.label)
    : (language === 'hu' ? opt.labelMobileHu : opt.labelMobile);

  const filterBar = (
    <div className="swipe-filters">
      <div className="swipe-filters__row">
        {TIME_OPTIONS.map(opt => (
          <button
            key={opt.value ?? 'any'}
            className={`swipe-filter-pill${maxTime === opt.value ? ' swipe-filter-pill--active' : ''}`}
            onClick={() => setMaxTime(opt.value)}
          >
            {getTimeLabel(opt)}
          </button>
        ))}
        {DIET_OPTIONS.map(opt => (
          <button
            key={opt.key}
            className={`swipe-filter-pill${dietFilters.has(opt.key) ? ' swipe-filter-pill--active' : ''}`}
            onClick={() => toggleDiet(opt.key)}
          >
            {getDietLabel(opt)}
          </button>
        ))}
      </div>
      {/* On desktop, diet options get their own row */}
    </div>
  );

  if (loading) {
    return (
      <div className="swipe-view">
        <button className="swipe-back-btn" onClick={onBack}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <p className="swipe-loading">{language === 'hu' ? 'Betöltés…' : 'Loading…'}</p>
      </div>
    );
  }

  const current = filteredRecipes[index];
  const isAlreadySaved = current && savedIds.has(current.id);

  return (
    <div className={`swipe-view${!isDesktop ? ' swipe-view--mobile' : ''}`}>
      <button className="swipe-back-btn" onClick={onBack} aria-label="Back">
        {isDesktop ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        )}
      </button>

      {/* EN/HU toggle — top-right on mobile, hidden on desktop (header handles it) */}
      {!isDesktop && (
        <div className="swipe-mobile-lang">
          <button
            className={`swipe-lang-btn${language === 'en' ? ' swipe-lang-btn--active' : ''}`}
            onClick={() => switchLanguage('en')}
          >EN</button>
          <button
            className={`swipe-lang-btn${language === 'hu' ? ' swipe-lang-btn--active' : ''}`}
            onClick={() => switchLanguage('hu')}
          >HU</button>
        </div>
      )}

      {isDesktop && (
        <>
          <h2 className="swipe-heading">
            {language === 'hu' ? 'Ma mit főzzünk?' : 'What shall we cook today?'}
          </h2>
          <p className="swipe-subheading">
            {language === 'hu' ? 'Böngéssz receptek között, és mentsd el a kedvenceidet' : 'Browse recipes and save the ones you like'}
          </p>
        </>
      )}

      {/* Filter bar */}
      {filterBar}

      {/* ════════════ DESKTOP LAYOUT ════════════ */}
      {isDesktop ? (
        <div className="swipe-desktop">
          {!current ? (
            <div className="swipe-empty">
              <p>{language === 'hu' ? 'Nincs találat a szűrőkre' : 'No recipes match your filters'}</p>
              <button className="swipe-reshuffle-btn" onClick={() => { setMaxTime(null); setDietFilters(new Set()); setIndex(0); }}>
                {language === 'hu' ? 'Szűrők törlése' : 'Clear filters'}
              </button>
            </div>
          ) : (
            <>
              <div className="swipe-desktop-card">
                {/* Image panel */}
                <div className="swipe-desktop-card__img-wrap">
                  {current.image_url
                    ? <img className="swipe-desktop-card__img" src={current.image_url} alt={current.title} />
                    : <div className="swipe-desktop-card__no-img">🍽️</div>
                  }
                </div>

                {/* Info panel */}
                <div className="swipe-desktop-card__info">
                  <span className="swipe-desktop-card__category">{current.category}</span>
                  <h3 className="swipe-desktop-card__title">{current.title}</h3>

                  <div className="swipe-desktop-card__meta">
                    {totalTime(current) && <span className="swipe-desktop-card__time">⏱ {totalTime(current)}</span>}
                  </div>

                  {current.description && (
                    <p className="swipe-desktop-card__desc">{current.description}</p>
                  )}

                  <div className="swipe-desktop-card__actions">
                    <button
                      className="swipe-desktop-view-btn"
                      onClick={() => onSelectRecipe(current.id)}
                      aria-label={language === 'hu' ? 'Recept megtekintése' : 'View recipe'}
                      title={language === 'hu' ? 'Recept megtekintése' : 'View recipe'}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                        <polyline points="15 3 21 3 21 9"/>
                        <line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                    </button>
                    <button
                      className={`swipe-desktop-save-btn${isAlreadySaved ? ' swipe-desktop-save-btn--saved' : ''}`}
                      onClick={() => toggleSave(current)}
                      aria-label={isAlreadySaved ? (language === 'hu' ? 'Mentve' : 'Saved') : (language === 'hu' ? 'Mentés' : 'Save')}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill={isAlreadySaved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="swipe-desktop-nav">
                <button
                  className="swipe-desktop-nav-btn"
                  onClick={goBack}
                  disabled={index === 0}
                  aria-label="Previous recipe"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <span className="swipe-counter">{index + 1} / {filteredRecipes.length}</span>
                <button
                  className="swipe-desktop-nav-btn"
                  onClick={advance}
                  disabled={index >= filteredRecipes.length - 1}
                  aria-label="Next recipe"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        /* ════════════ MOBILE BROWSE LAYOUT ════════════ */
        <div className="swipe-mobile">
          {!current ? (
            <div className="swipe-empty">
              <p>{language === 'hu' ? 'Nincs találat a szűrőkre' : 'No recipes match your filters'}</p>
              <button className="swipe-reshuffle-btn" onClick={() => { setMaxTime(null); setDietFilters(new Set()); setIndex(0); }}>
                {language === 'hu' ? 'Szűrők törlése' : 'Clear filters'}
              </button>
            </div>
          ) : (
            <div className="swipe-card" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
              {/* Image with nav arrows at the bottom */}
              <div className="swipe-card__image" style={{ backgroundImage: current.image_url ? `url(${current.image_url})` : 'none' }}>
                {!current.image_url && <div className="swipe-card__no-image">🍽️</div>}
                <button
                  className="swipe-nav-arrow swipe-nav-arrow--left"
                  onClick={goBack}
                  disabled={index === 0}
                  aria-label="Previous"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <button
                  className="swipe-nav-arrow swipe-nav-arrow--right"
                  onClick={advance}
                  disabled={index >= filteredRecipes.length - 1}
                  aria-label="Next"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </div>

              {/* Info panel */}
              <div className="swipe-card__info">
                <span className="swipe-card__category">{current.category}</span>
                <h3 className="swipe-card__title">{current.title}</h3>
                <div className="swipe-card__meta">
                  {totalTime(current) && <span className="swipe-card__time">⏱ {totalTime(current)}</span>}
                </div>
                {current.description && <p className="swipe-card__desc">{current.description}</p>}
              </div>

              {/* Action bar */}
              <div className="swipe-card__actions">
                <button className="swipe-btn swipe-btn--info" onClick={() => onSelectRecipe(current.id)} aria-label={language === 'hu' ? 'Recept megtekintése' : 'View recipe'}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                </button>
                <span className="swipe-counter">{index + 1} / {filteredRecipes.length}</span>
                <button
                  className={`swipe-btn swipe-btn--save${isAlreadySaved ? ' swipe-btn--saved' : ''}`}
                  onClick={() => toggleSave(current)}
                  aria-label={isAlreadySaved ? (language === 'hu' ? 'Mentés törlése' : 'Unsave') : (language === 'hu' ? 'Mentés' : 'Save')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill={isAlreadySaved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
